import Phaser from 'phaser';
import { BaseScene } from '@core/scenes/BaseScene';
import { SceneKeys, Palette } from '@core/config/constants';
import { TextButton } from '@ui/components/TextButton';
import { CycleSelector } from '@ui/components/CycleSelector';
import {
  DressUpConfig,
  DRESS_COLORS,
  BACKGROUND_COLORS,
  ACCESSORIES,
} from '@modules/dressup/config';

const HEAD_EMOJI = '👧';

interface Point {
  readonly x: number;
  readonly y: number;
}

/**
 * Minijuego creativo de estilo: el jugador arma un look eligiendo color de vestido,
 * accesorio y fondo, y lo ve reflejado en el personaje en tiempo real. Al guardar el
 * look gana estrellas. El layout se adapta: en vertical apila personaje + selectores,
 * en horizontal los muestra en dos columnas.
 */
export class DressUpScene extends BaseScene {
  private dressIndex = 0;
  private accessoryIndex = 0;
  private backgroundIndex = 0;

  private backgroundRect!: Phaser.GameObjects.Rectangle;
  private dressGraphics!: Phaser.GameObjects.Graphics;
  private accessoryText!: Phaser.GameObjects.Text;
  private coinsText!: Phaser.GameObjects.Text;

  private characterX = 0;
  private characterY = 0;

  public constructor() {
    super(DressUpConfig.key);
  }

  public create(): void {
    this.initResponsive();
    this.resetState();

    const { width, height } = this.scale;

    this.backgroundRect = this.add.rectangle(
      this.centerX,
      this.centerY,
      width,
      height,
      BACKGROUND_COLORS[this.backgroundIndex].value,
    );

    this.addTwinkles(20);

    this.add
      .text(this.centerX, height * 0.06, DressUpConfig.title, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '40px',
        color: Palette.textLight,
      })
      .setOrigin(0.5);

    this.coinsText = this.add
      .text(width - 28, 24, '', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '30px',
        color: Palette.textLight,
      })
      .setOrigin(1, 0);

    this.buildLayout();
    this.redrawCharacter();
    this.refreshCoins();
  }

  private resetState(): void {
    this.dressIndex = 0;
    this.accessoryIndex = 0;
    this.backgroundIndex = 0;
  }

  private buildLayout(): void {
    const { width, height } = this.scale;

    if (this.isPortrait) {
      this.characterX = this.centerX;
      this.characterY = height * 0.26;
      const selectorWidth = Math.min(380, width * 0.82);
      const selectorYs = [height * 0.48, height * 0.62, height * 0.76];

      this.createCharacter();
      this.createSelectors(this.centerX, selectorYs, selectorWidth);
      this.createButtons(
        { x: this.centerX, y: height - 150 },
        { x: this.centerX, y: height - 74 },
      );
      return;
    }

    this.characterX = width * 0.3;
    this.characterY = height * 0.56;
    const selectorWidth = Math.min(360, width * 0.4);
    const selectorX = Math.min(width * 0.7, width - selectorWidth / 2 - 24);
    const selectorYs = [height * 0.3, height * 0.52, height * 0.74];

    this.createCharacter();
    this.createSelectors(selectorX, selectorYs, selectorWidth);
    this.createButtons(
      { x: this.characterX, y: height - 80 },
      { x: selectorX, y: height - 80 },
    );
  }

  private createCharacter(): void {
    this.add.ellipse(this.characterX, this.characterY + 30, 320, 470, 0xffffff, 0.1);

    this.dressGraphics = this.add.graphics();

    this.add
      .text(this.characterX, this.characterY - 60, HEAD_EMOJI, { fontSize: '120px' })
      .setOrigin(0.5);

    this.accessoryText = this.add
      .text(this.characterX, this.characterY - 150, '', { fontSize: '72px' })
      .setOrigin(0.5);
  }

  private createSelectors(selectorX: number, ys: number[], width: number): void {
    new CycleSelector(this, {
      x: selectorX,
      y: ys[0],
      width,
      title: 'Vestido',
      labels: DRESS_COLORS.map((color) => color.name),
      onChange: (index: number) => {
        this.dressIndex = index;
        this.redrawCharacter();
      },
    });

    new CycleSelector(this, {
      x: selectorX,
      y: ys[1],
      width,
      title: 'Accesorio',
      labels: ACCESSORIES.map((accessory) => accessory.name),
      onChange: (index: number) => {
        this.accessoryIndex = index;
        this.redrawCharacter();
      },
    });

    new CycleSelector(this, {
      x: selectorX,
      y: ys[2],
      width,
      title: 'Fondo',
      labels: BACKGROUND_COLORS.map((color) => color.name),
      onChange: (index: number) => {
        this.backgroundIndex = index;
        this.redrawCharacter();
      },
    });
  }

  private createButtons(save: Point, back: Point): void {
    new TextButton(this, {
      x: save.x,
      y: save.y,
      label: 'Guardar look ✨',
      color: Palette.primary,
      onClick: () => this.saveLook(),
    });

    new TextButton(this, {
      x: back.x,
      y: back.y,
      label: 'Volver al menú',
      color: Palette.accent,
      onClick: () => this.scene.start(SceneKeys.Hub),
    });
  }

  private redrawCharacter(): void {
    this.backgroundRect.setFillStyle(BACKGROUND_COLORS[this.backgroundIndex].value);
    this.accessoryText.setText(ACCESSORIES[this.accessoryIndex].emoji);
    this.drawDress(DRESS_COLORS[this.dressIndex].value);
  }

  private drawDress(color: number): void {
    const x = this.characterX;
    const y = this.characterY;

    this.dressGraphics.clear();
    this.dressGraphics.fillStyle(color, 1);

    this.dressGraphics.fillRoundedRect(x - 38, y + 6, 76, 70, 16);
    this.dressGraphics.fillTriangle(x - 95, y + 170, x + 95, y + 170, x, y + 30);
    this.dressGraphics.fillCircle(x, y + 6, 22);
  }

  private saveLook(): void {
    this.profile.addCoins(DressUpConfig.coinsPerLook);
    this.audio.playReward();
    this.refreshCoins();
    this.showSavedFeedback();
  }

  private showSavedFeedback(): void {
    const message = this.add
      .text(this.centerX, this.scale.height * 0.16, `¡Look guardado! +${DressUpConfig.coinsPerLook} ⭐`, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '30px',
        color: '#ffffff',
        backgroundColor: '#00000055',
        padding: { x: 16, y: 8 },
      })
      .setOrigin(0.5);

    this.tweens.add({
      targets: message,
      y: message.y - 60,
      alpha: 0,
      duration: 1200,
      ease: 'Cubic.easeOut',
      onComplete: () => message.destroy(),
    });
  }

  private refreshCoins(): void {
    this.coinsText.setText(`⭐ ${this.profile.coins}`);
  }
}
